/**
 * «Platforma haqida» (ЧТЗ 4.2.11.1, 1-blok). Tartib: platforma ta'rifi →
 * maqsad va vazifalar (sxema) → kimlar uchun → asosiy imkoniyatlar → ochiq va
 * OneID talab qilinadigan xizmatlar → ochiq ko'rsatkichlar (davr, manba, sana)
 * → mas'ul tashkilot, rahbariyat va tuzilma → foydalanishga yordam.
 *
 * Matnlar, rasmlar, ko'rsatkichlar va havolalar superadmin panelidan
 * (`Page`, `AboutItem`, `PublicStatistic`, `LeadershipMember`, `OrgUnit`,
 * `PortalSettings`) uz/ru/en tillarida boshqariladi. Ma'lumot kiritilmagan
 * blok yashiriladi yoki rasmiy manbaga havola beriladi.
 */
import { useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

import type {
  PortalAboutItem,
  PortalLeader,
  PortalOrgUnit,
  PublicStatistic,
  PublicStatisticsCharts,
} from "@/shared/api/types";
import { aboutFallback, statisticsFallback, structureFallback } from "@/shared/data/landing";
import type { StatisticItem } from "@/shared/data/landing";
import { useI18n } from "@/shared/i18n";
import { useBalancedGrid } from "@/shared/lib/balancedGrid";
import { formatDate } from "@/shared/lib/format";
import { FAQ_HREF, GUIDES_HREF } from "@/shared/lib/sectionRoutes";
import { sanitizeHtml } from "@/shared/lib/sanitize";
import { CountUp } from "@/shared/ui/CountUp";
import { Icon, isIconName } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { AboutFlows } from "./AboutFlows";
import { GuidesSection } from "./GuidesSection";
import { SplitHead } from "./SplitHead";
import { formatStat, usePortalAbout } from "./publicData";

const KNOWN_ICONS = new Set<string>([
  "tree", "globe", "leaf", "sparkles", "package", "trending", "briefcase", "checkCircle",
  "flame", "map", "layers", "activity", "chart", "shield", "users",
]);

export function toStatisticItem(item: PublicStatistic): StatisticItem {
  return {
    code: item.code,
    label: item.label,
    value: item.value,
    unit: item.unit,
    icon: (KNOWN_ICONS.has(item.icon) ? item.icon : "leaf") as IconName,
    display: item.display,
    max: item.max_value || 100,
    source: item.source,
    asOf: item.as_of,
    period: item.period ?? "",
    showInHero: item.show_in_hero,
    showOnGlobe: item.show_on_globe ?? true,
    globeLabel: item.globe_label ?? "",
  };
}

/** Umumiy statistik kartochkalar ro'yxati: server bo'lsa — undan, aks holda namunaviy. */
export function useStatistics(): {
  items: StatisticItem[];
  charts: PublicStatisticsCharts | null;
  sample: boolean;
  loading: boolean;
} {
  const about = usePortalAbout();
  return useMemo(() => {
    if (about.data) {
      return {
        items: about.data.statistics.map(toStatisticItem),
        charts: about.data.statistics_charts ?? null,
        sample: false,
        loading: false,
      };
    }
    return { items: statisticsFallback, charts: null, sample: about.offline, loading: !about.offline };
  }, [about]);
}

/* ------------------------------------------------------------ infografika */

function Ring({ value, max, label }: { value: number; max: number; label: string }) {
  const ratio = Math.max(0, Math.min(1, max ? value / max : 0));
  const circumference = 2 * Math.PI * 26;
  return (
    <svg className="stat-ring" width="68" height="68" viewBox="0 0 68 68" role="img" aria-label={label}>
      <circle className="stat-ring-track" cx="34" cy="34" r="26" />
      <circle
        className="stat-ring-fill"
        cx="34"
        cy="34"
        r="26"
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: circumference * (1 - ratio),
        }}
      />
    </svg>
  );
}

export function StatsInfographic({
  items,
  sample,
}: {
  items: StatisticItem[];
  sample: boolean;
}) {
  const { t, lang, locale } = useI18n();
  const grid = useBalancedGrid(items.length, { min: 210, gap: 12, minCols: 2 });
  if (items.length === 0) return null;

  return (
    <div className="stats-block" data-reveal>
      <div className="stats-head">
        <h3>
          <Icon name="chart" size={20} />
          {t("about.stats")}
        </h3>
        <p className="hint">
          {sample
            ? "Namunaviy ko'rsatkichlar — server bilan aloqa tiklanganda tizim ma'lumotlari ko'rsatiladi."
            : t("about.statsLead")}
        </p>
      </div>
      <ul className="stats-grid" ref={grid.ref as React.RefObject<HTMLUListElement>} style={grid.style}>
        {items.map((item, index) => {
          const span = grid.spanFor(index);
          const text = formatStat(item.value, lang);
          const ratio = item.value !== null && item.max ? Math.min(item.value / item.max, 1) : 0;
          return (
            <li
              className={`stat-card is-${item.display}`}
              key={item.code}
              style={
                {
                  "--reveal-delay": `${index * 60}ms`,
                  ...(span > 1 ? { gridColumn: `span ${span}` } : {}),
                } as CSSProperties
              }
            >
              <span className="stat-icon" aria-hidden="true">
                <Icon name={item.icon} size={20} />
              </span>
              <div className="stat-value">
                <b className="num">
                  {item.value === null ? "—" : <CountUp value={text} />}
                </b>
                {item.unit ? <small>{item.unit}</small> : null}
              </div>
              <span className="stat-label">{item.label}</span>
              {item.display === "percent" && item.value !== null ? (
                <span
                  className="stat-bar"
                  role="progressbar"
                  aria-valuenow={item.value}
                  aria-valuemin={0}
                  aria-valuemax={item.max}
                  aria-label={item.label}
                >
                  <span style={{ width: `${ratio * 100}%` }} />
                </span>
              ) : null}
              {item.display === "gauge" && item.value !== null ? (
                <Ring value={item.value} max={item.max} label={`${item.label}: ${text}`} />
              ) : null}
              {!sample && (item.period || item.source || item.asOf) ? (
                <dl className="stat-meta">
                  {item.period ? (
                    <div>
                      <dt>{t("about.period")}</dt>
                      <dd>{item.period}</dd>
                    </div>
                  ) : null}
                  {item.source ? (
                    <div>
                      <dt>{t("about.source")}</dt>
                      <dd>{item.source}</dd>
                    </div>
                  ) : null}
                  {item.asOf ? (
                    <div>
                      <dt>{t("about.updated")}</dt>
                      <dd>{formatDate(item.asOf, locale)}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------ rahbariyat */

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function Leadership({ leaders, officialUrl }: { leaders: PortalLeader[]; officialUrl: string }) {
  const { t } = useI18n();
  if (leaders.length === 0) {
    /* Ma'lumot hali kiritilmagan — ichki izoh o'rniga rasmiy manbaga havola. */
    return (
      <div className="org-empty">
        <Icon name="users" size={22} />
        <p>{t("about.leadersOfficial")}</p>
        {officialUrl ? (
          <a className="btn btn-outline btn-sm" href={officialUrl} target="_blank" rel="noreferrer noopener">
            {t("about.leadersLink")}
            <Icon name="external" size={14} />
          </a>
        ) : null}
      </div>
    );
  }
  return (
    <ul className="leaders">
      {leaders.map((leader) => (
        <li className={`leader${leader.is_head ? " is-head" : ""}`} key={leader.id}>
          {leader.photo_link ? (
            <img
              className="leader-photo"
              src={leader.photo_link}
              alt={leader.full_name}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="leader-photo is-initials" aria-hidden="true">
              {initials(leader.full_name)}
            </span>
          )}
          <div className="leader-body">
            <b>{leader.full_name}</b>
            <span className="leader-position">{leader.position}</span>
            {leader.reception_hours ? (
              <span className="leader-meta">
                <Icon name="clock" size={14} />
                Qabul: {leader.reception_hours}
              </span>
            ) : null}
            {leader.phone ? (
              <a className="leader-meta" href={`tel:${leader.phone.replace(/\s/g, "")}`}>
                <Icon name="phone" size={14} />
                {leader.phone}
              </a>
            ) : null}
            {leader.bio ? (
              <details className="leader-bio">
                <summary>Tarjimai hol</summary>
                <div className="rich-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(leader.bio) }} />
              </details>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------ tuzilma */

const KIND_TITLES: Record<PortalOrgUnit["kind"], string> = {
  agency: "Agentlik",
  central: "Markaziy apparat bo'linmalari",
  regional: "Hududiy boshqarmalar",
  enterprise: "Tizimdagi korxona va tashkilotlar",
};

const COLLAPSED = 8;

function UnitGroup({ title, units }: { title: string; units: PortalOrgUnit[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? units : units.slice(0, COLLAPSED);
  return (
    <div className="org-group">
      <h4>
        {title} <span className="num">· {units.length}</span>
      </h4>
      <ul className="org-units">
        {visible.map((unit) => (
          <li key={unit.id}>
            <Icon name="building" size={16} />
            <span>
              <b>{unit.title}</b>
              {unit.head_title || unit.phone ? (
                <small>
                  {[unit.head_title, unit.phone].filter(Boolean).join(" · ")}
                </small>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {units.length > COLLAPSED ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Qisqartirish" : `Barchasini ko'rsatish (${units.length})`}
          <Icon name="chevronDown" size={14} className={expanded ? "flip-y" : undefined} />
        </button>
      ) : null}
    </div>
  );
}

function Structure({ units }: { units: PortalOrgUnit[] }) {
  /* Server bo'lmasa — tashkilotlar reyestridagi tuzilma (agentlik → 14 boshqarma). */
  const tree: PortalOrgUnit[] = units.length
    ? units
    : [
        {
          id: 0,
          kind: "agency",
          title: structureFallback.root,
          description: "",
          head_title: structureFallback.rootHead,
          phone: "",
          email: "",
          address: "",
          children: structureFallback.regional.map((title, index) => ({
            id: index + 1,
            kind: "regional" as const,
            title,
            description: "",
            head_title: "Boshqarma boshlig'i",
            phone: "",
            email: "",
            address: "",
            children: [],
          })),
        },
      ];

  return (
    <div className="org-tree">
      {tree.map((root) => {
        const groups = (["central", "regional", "enterprise", "agency"] as const)
          .map((kind) => ({ kind, units: root.children.filter((child) => child.kind === kind) }))
          .filter((group) => group.units.length > 0);
        return (
          <div className="org-root" key={root.id}>
            <div className="org-root-card">
              <span className="org-root-icon" aria-hidden="true">
                <Icon name="shield" size={22} />
              </span>
              <div>
                <b>{root.title}</b>
                {root.head_title ? <span>{root.head_title}</span> : null}
                {root.description ? <p>{root.description}</p> : null}
              </div>
            </div>
            <div className="org-groups">
              {groups.map((group) => (
                <UnitGroup key={group.kind} title={KIND_TITLES[group.kind]} units={group.units} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** CMS matnining birinchi xatboshisi — sarlavha ostidagi qisqa izoh. */
function firstParagraph(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.querySelector("p")?.textContent ?? doc.body.textContent ?? "").trim();
}

/* ------------------------------------------------------------ bloklar */

const ACCESS_ICON: Record<PortalAboutItem["access"], IconName> = {
  open: "eye",
  oneid: "lock",
  mixed: "user",
  staff: "shield",
};

function itemIcon(item: PortalAboutItem, fallback: IconName): IconName {
  return isIconName(item.icon) ? item.icon : fallback;
}

function AccessBadge({ access }: { access: PortalAboutItem["access"] }) {
  const { t } = useI18n();
  return (
    <span className={`badge about-access is-${access}`}>
      <Icon name={ACCESS_ICON[access]} size={12} />
      {t(`about.access.${access}`)}
    </span>
  );
}

/** Eski `/yoriqnomalar` va `/savol-javob` havolalari (CMS) — endi shu sahifaning yo'riqnomalar bloki. */
const LEGACY_LINKS: Record<string, string> = { "/yoriqnomalar": GUIDES_HREF, "/savol-javob": FAQ_HREF };

/** Ichki (`/…`) havola — SPA ichida, tashqi — yangi oynada. */
function SmartLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  if (/^https?:\/\//i.test(to)) {
    return (
      <a className={className} href={to} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={LEGACY_LINKS[to] ?? to}>
      {children}
    </Link>
  );
}

function Block({ id, title, lead, children }: { id: string; title?: string; lead?: string; children: ReactNode }) {
  return (
    <section className="about-block" aria-labelledby={title ? `${id}-title` : undefined} data-reveal>
      {title || lead ? (
        <div className="about-block-head">
          {title ? <h3 id={`${id}-title`}>{title}</h3> : null}
          {lead ? <p className="hint">{lead}</p> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function AboutSection() {
  const { t } = useI18n();
  const about = usePortalAbout();
  const statistics = useStatistics();
  const [tab, setTab] = useState<"leadership" | "structure">("leadership");

  const page = about.data?.pages?.about;
  const agencyPage = about.data?.pages?.agency;
  const agency = about.data?.agency;
  const sections = about.data?.sections ?? {};
  const tasks = sections.task ?? [];
  const audience = sections.audience ?? [];
  const openItems = sections.open ?? [];
  const oneidItems = sections.oneid ?? [];
  const help = sections.help ?? [];
  const leaders = about.data?.leaders ?? [];
  const units = about.data?.structure ?? [];

  const lead = useMemo(
    () => page?.summary || firstParagraph(sanitizeHtml(page?.body ?? "")) || aboutFallback.purpose,
    [page?.summary, page?.body],
  );

  return (
    <section className="section section-alt" id="about" aria-labelledby="about-title">
      <div className="container about-page">
        <SplitHead
          scene="about"
          side="right"
          eyebrow={t("nav.about")}
          title={page?.title || aboutFallback.title}
          titleId="about-title"
          lead={lead}
        />

        {about.data ? (
          <Block id="about-purpose">
            {tasks.length ? (
              <ul className="about-tasks">
                {tasks.map((item) => (
                  <li key={item.code}>
                    <Icon name={itemIcon(item, "leaf")} size={18} />
                    <span>
                      <b>{item.title}</b>
                      {item.text ? <small>{item.text}</small> : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            <AboutFlows />
          </Block>
        ) : null}

        {audience.length ? (
          <Block id="about-audience" title={t("about.audience")}>
            <div className="about-audience">
              {audience.map((item) => (
                <article className="about-card" key={item.code}>
                  <div className="about-card-head">
                    <span className="pillar-icon" aria-hidden="true">
                      <Icon name={itemIcon(item, "users")} size={22} />
                    </span>
                    <div>
                      <h4>{item.title}</h4>
                      {item.text ? <p>{item.text}</p> : null}
                    </div>
                  </div>
                  {item.bullets.length ? (
                    <ul className="about-bullets">
                      {item.bullets.map((line) => (
                        <li key={line}>
                          <Icon name="check" size={14} />
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="about-card-foot">
                    <AccessBadge access={item.access} />
                    {item.link_url ? (
                      <SmartLink className="btn btn-primary btn-sm" to={item.link_url}>
                        {item.link_label || t("about.open")}
                        <Icon name="arrowRight" size={14} />
                      </SmartLink>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </Block>
        ) : null}

        {openItems.length || oneidItems.length ? (
          <Block id="about-access" title={t("about.access")} lead={t("about.accessLead")}>
            <div className="about-access-cols">
              {[
                { key: "open", title: t("about.openTitle"), icon: "eye" as IconName, items: openItems },
                { key: "oneid", title: t("about.oneidTitle"), icon: "lock" as IconName, items: oneidItems },
              ].map((column) =>
                column.items.length ? (
                  <div className={`about-access-col is-${column.key}`} key={column.key}>
                    <h4>
                      <Icon name={column.icon} size={16} />
                      {column.title}
                    </h4>
                    <ul>
                      {column.items.map((item) => (
                        <li key={item.code}>
                          {item.link_url ? (
                            <SmartLink to={item.link_url}>
                              <Icon name={itemIcon(item, "check")} size={14} />
                              {item.title}
                            </SmartLink>
                          ) : (
                            <span>
                              <Icon name={itemIcon(item, "check")} size={14} />
                              {item.title}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null,
              )}
            </div>
          </Block>
        ) : null}

        {statistics.items.length && !statistics.loading ? (
          <StatsInfographic items={statistics.items} sample={statistics.sample} />
        ) : null}

        <div className="org-card" data-reveal>
          <div className="org-card-head">
            <div>
              <span className="eyebrow">{t("about.agency")}</span>
              <h3>{agencyPage?.title || structureFallback.root}</h3>
            </div>
          </div>
          {agencyPage?.body || agency ? (
            <div className="about-agency">
              {agencyPage?.body ? (
                <div>
                  <h4>{t("about.tasks")}</h4>
                  <div className="rich-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(agencyPage.body) }} />
                </div>
              ) : null}
              {agency ? (
                <div className="about-agency-contacts">
                  {agency.phone ? (
                    <a href={`tel:${agency.phone.replace(/[^\d+]/g, "")}`}>
                      <Icon name="phone" size={16} />
                      {agency.phone}
                    </a>
                  ) : null}
                  {agency.email ? (
                    <a href={`mailto:${agency.email}`}>
                      <Icon name="mail" size={16} />
                      {agency.email}
                    </a>
                  ) : null}
                  {agency.address ? (
                    <span>
                      <Icon name="pin" size={16} />
                      {agency.address}
                    </span>
                  ) : null}
                  <div className="about-agency-links">
                    {agency.website ? (
                      <a className="btn btn-outline btn-sm" href={agency.website} target="_blank" rel="noreferrer noopener">
                        {t("about.website")}
                        <Icon name="external" size={14} />
                      </a>
                    ) : null}
                    <Link className="btn btn-ghost btn-sm" to="/aloqa">
                      {t("about.contacts")}
                      <Icon name="arrowRight" size={14} />
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="org-card-tabs">
            <div className="segmented" role="tablist" aria-label={t("about.agency")}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "leadership"}
                aria-pressed={tab === "leadership"}
                onClick={() => setTab("leadership")}
              >
                {t("about.leadership")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "structure"}
                aria-pressed={tab === "structure"}
                onClick={() => setTab("structure")}
              >
                {t("about.structure")}
              </button>
            </div>
          </div>
          <div role="tabpanel">
            {tab === "leadership" ? (
              <Leadership leaders={leaders} officialUrl={agency?.leadership_url ?? ""} />
            ) : (
              <Structure units={units} />
            )}
          </div>
        </div>

        <GuidesSection embedded />

        {help.length ? (
          <Block id="about-help" title={t("about.help")}>
            <div className="about-help">
              {help.map((item) => (
                <SmartLink className="about-help-card" to={item.link_url || "/"} key={item.code}>
                  <span className="pillar-icon" aria-hidden="true">
                    <Icon name={itemIcon(item, "help")} size={20} />
                  </span>
                  <span>
                    <b>{item.title}</b>
                    {item.text ? <small>{item.text}</small> : null}
                  </span>
                  <Icon name="arrowRight" size={16} />
                </SmartLink>
              ))}
            </div>
          </Block>
        ) : null}
      </div>
    </section>
  );
}
